const destinations = [
  {
    name: "Germany",
    context: "UNL study-abroad programs in Berlin, Munich, and Heidelberg; state trade missions to manufacturing and agricultural sectors; family heritage travel from Nebraska's strong German-American community",
    urgency: "Study-abroad deadlines and trade mission schedules have fixed, non-negotiable dates",
    icon: "ri-map-pin-2-line",
    flag: "DE",
  },
  {
    name: "United Kingdom",
    context: "Academic conferences and research collaborations; state government trade delegations; UNL exchange programs with UK universities; family heritage travel",
    urgency: "Conference presentation dates and government meetings cannot be rescheduled",
    icon: "ri-map-pin-2-line",
    flag: "GB",
  },
  {
    name: "Japan & South Korea",
    context: "Nebraska Department of Economic Development trade missions; UNL engineering and agriculture research partnerships; agribusiness and commodity export meetings",
    urgency: "Trade missions operate on fixed itineraries with partner governments — dates are immovable",
    icon: "ri-map-pin-2-line",
    flag: "JP",
  },
  {
    name: "Italy & Mediterranean",
    context: "UNL classics, art history, and architecture study-abroad programs in Rome and Florence; faculty research at archaeological sites; heritage tourism from Nebraska's Italian-American community",
    urgency: "Academic program orientations have fixed start dates — late passports mean missed semesters",
    icon: "ri-map-pin-2-line",
    flag: "IT",
  },
  {
    name: "Canada",
    context: "Agricultural conferences and trade shows; university research partnerships with Canadian institutions; family vacations to the Canadian Rockies and Toronto",
    urgency: "Summer conference and vacation bookings peak May through August — standard processing times spike",
    icon: "ri-map-pin-2-line",
    flag: "CA",
  },
  {
    name: "China & Southeast Asia",
    context: "Nebraska agricultural export missions; UNL Confucius Institute exchanges; state trade delegations; business and manufacturing partnerships",
    urgency: "Visa requirements add additional processing time — passport must be valid well before visa application",
    icon: "ri-map-pin-2-line",
    flag: "CN",
  },
];

const commonSituations = [
  {
    stat: "55%",
    label: "of Lincoln expedited requests are university-related — study abroad students, faculty conferences, and research travel",
  },
  {
    stat: "3–6 Weeks",
    label: "average notice Lincoln travelers have before a planned international trip, study-abroad orientation, or trade mission",
  },
  {
    stat: "Nov–Mar",
    label: "peak season when winter weather makes I-80 driving to Minneapolis or Denver passport agencies hazardous",
  },
  {
    stat: "6 Days",
    label: "shortest turnaround we have accommodated for a UNL student with an unexpected study-abroad acceptance to Prague",
  },
];

export default function LincolnDestinationsSection() {
  return (
    <section className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Travel Intelligence</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Where Lincoln Travelers Go — And Why Passports Get Urgent
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            These are the destinations and travel patterns we see most from Southeast Nebraska clients. Understanding local travel behavior is part of why our document review catches issues before they derail your study abroad, trade mission, or family trip.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {commonSituations.map((s, i) => (
            <div key={i} className="bg-[#1A365D] rounded-lg p-6 text-center">
              <p className="font-serif text-3xl font-bold text-[#F6C344] mb-2">{s.stat}</p>
              <p className="text-white/60 text-xs leading-relaxed">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {destinations.map((d, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 group-hover:bg-[#F6C344]/15 transition-colors flex-shrink-0">
                  <i className={`${d.icon} text-[#1A365D] group-hover:text-[#F6C344] transition-colors text-lg`}></i>
                </div>
                <div>
                  <h3 className="font-bold text-[#1A365D] text-sm">{d.name}</h3>
                </div>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">
                {d.context}
              </p>
              <div className="flex items-start gap-2">
                <i className="ri-time-line text-[#F6C344] text-xs flex-shrink-0 mt-0.5"></i>
                <p className="text-gray-400 text-xs leading-relaxed italic">
                  {d.urgency}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
              <i className="ri-links-line text-[#1A365D] text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">Explore Other Nebraska Service Areas</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                We serve travelers across Nebraska with the same expedited passport processing and document review expertise. Looking for service in another community?
              </p>
            </div>
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <a href="/omaha" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Omaha <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/grand-island" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Grand Island <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/kearney" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Kearney <i className="ri-arrow-right-line text-xs"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}